import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
  Card,
  Divider,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  CircularProgress,
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Chip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {GET, POST} from "APIclient";
import properties from "constants/ApplicationConstants";

function AssignKRA() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({open: false, message: "", type: ""});

  const [permission, setPermission] = useState(null);
  const [appraisalId, setAppraisalId] = useState(null);
  const [managerId, setManagerId] = useState(null);

  const [competencyPrimary, setCompetencyPrimary] = useState([]);
  const [competencySecondary, setCompetencySecondary] = useState([]);
  const [technicalSecondary, setTechnicalSecondary] = useState([]);

  const [technicalPrimary, setTechnicalPrimary] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);

  const [employeeList, setEmployeeList] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  useEffect(() => {
    const empId = localStorage.getItem("empId") || sessionStorage.getItem("empId");
    if (empId) {
      const parsedEmpId = parseInt(empId);
      setManagerId(parsedEmpId);
      initializeData(parsedEmpId);
    } else {
      showNotification("Manager ID not found", "error");
      setLoading(false);
    }
  }, []);

  const initializeData = async empId => {
    try {
      setLoading(true);
      const permissionResponse = await fetchAppraisalPermission(empId);
      if (permissionResponse) {
        await Promise.all([fetchAllSkills(empId), fetchEmployeeList(empId)]);
      }
    } catch (error) {
      console.error("Error initializing data:", error);
      showNotification("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAppraisalPermission = async managerId => {
    try {
      const response = await GET(`manager/appraisal/current`);
      if (response.data && response.data.data) {
        const permissionData = response.data.data;
        setPermission(permissionData);
        setAppraisalId(permissionData.appraisalId);
        return permissionData;
      }
      return null;
    } catch (error) {
      console.error("Error fetching appraisal permission:", error);
      setPermission(null);
      showNotification(
        error.response?.data?.message || "Failed to load appraisal window details",
        "error"
      );
      return null;
    }
  };

  const fetchAllSkills = async empId => {
    try {
      const response = await GET(`manager/list-kraSkills/${empId}`);
      if (response.data && response.data.data) {
        const skillsData = response.data.data;
        const compPrimary = [];
        const compSecondary = [];
        const techSecondary = [];
        const techPrimary = [];

        skillsData.forEach(skill => {
          if (skill.status !== 1) return;
          const skillType = skill.skillTypes.toLowerCase().trim().replace(/[-\s]/g, "");
          const skillObj = {paFormId: skill.paFormId, skillSet: skill.skillSet};

          if (skillType === "competencyprimary" || skillType === "performancecompetencyprimary") {
            compPrimary.push(skillObj);
          } else if (
            skillType === "competencysecondary" ||
            skillType === "performancecompetencysecondary"
          ) {
            compSecondary.push(skillObj);
          } else if (skillType === "technicalsecondary") {
            techSecondary.push(skillObj);
          } else if (skillType === "technicalprimary") {
            techPrimary.push(skillObj);
          }
        });

        setCompetencyPrimary(compPrimary);
        setCompetencySecondary(compSecondary);
        setTechnicalSecondary(techSecondary);
        setTechnicalPrimary(techPrimary);
      } else {
        showNotification("No skills found", "warning");
      }
    } catch (error) {
      console.error("Error fetching skills:", error);
      showNotification(error.response?.data?.message || "Failed to load skills", "error");
    }
  };

  const fetchEmployeeList = async managerId => {
    try {
      const response = await GET(`manager/reportees/${managerId}`);
      if (response.data && response.data.data) {
        const employeesData = response.data.data.paginatedData || response.data.data;
        const employees = Array.isArray(employeesData)
          ? employeesData.map(emp => ({
              empId: emp.empId,
              firstName: emp.firstName || emp.first_name || "",
              middleName: emp.middleName || emp.middle_name || "",
              lastName: emp.lastName || emp.last_name || "",
              officialEmailAddress: emp.officialEmailAddress || emp.official_email_address || "",
              designation: emp.designation || "",
              department: emp.department || "",
              applicableSkills: emp?.applicableSkills || null
            }))
          : [];

        const availableEmployees = employees.filter(
          emp => emp?.applicableSkills?.length === 0 || emp?.applicableSkills == null
        );

        setEmployeeList(availableEmployees);
        if (availableEmployees?.length === 0) {
          showNotification("All reporting employees already have KRA assigned", "info");
        }
      } else {
        setEmployeeList([]);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      showNotification(error.response?.data?.message || "Failed to load employees", "error");
      setEmployeeList([]);
    }
  };

  const handleSkillToggle = paFormId => {
    setSelectedSkills(prev =>
      prev.includes(paFormId) ? prev.filter(id => id !== paFormId) : [...prev, paFormId]
    );
  };

  const handleSelectAllSkills = () => {
    if (selectedSkills.length === technicalPrimary.length) {
      setSelectedSkills([]);
    } else {
      setSelectedSkills(technicalPrimary.map(skill => skill.paFormId));
    }
  };

  const availableEmployeeOptions = employeeList.filter(
    emp => !selectedEmployees.some(selected => selected.empId === emp.empId)
  );

  const handleSubmit = () => {
    if (selectedSkills.length === 0) {
      showNotification("Please select at least one technical skill", "warning");
      return;
    }
    if (selectedEmployees.length === 0) {
      showNotification("Please select at least one employee", "warning");
      return;
    }
    navigate("/manager/appraisal/confirmation", {
      state: {
        appraisalId,
        selectedSkills,
        selectedEmployees,
        technicalPrimary,
        competencyPrimary,
        competencySecondary,
        technicalSecondary
      }
    });
  };

  const showNotification = (message, type) => {
    setNotification({open: true, message, type});
    setTimeout(() => {
      setNotification({open: false, message: "", type: ""});
    }, 4000);
  };

  const isDeadlinePassed = () => {
    if (!permission || !permission.formEditDeadline) return false;
    const deadline = new Date(permission.formEditDeadline);
    const today = new Date();
    const deadlineDateOnly = new Date(
      deadline.getFullYear(),
      deadline.getMonth(),
      deadline.getDate()
    );
    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return todayDateOnly > deadlineDateOnly;
  };

  const renderReadOnlySkillSection = (title, skills) => {
    if (skills.length === 0) return null;
    return (
      <MDBox mb={0.5}>
        <MDTypography variant="subtitle2" fontWeight="medium" mb={0.25}>
          {title}
        </MDTypography>
        <Grid container spacing={0.25}>
          {skills.map((skill, index) => (
            <Grid item xs={12} sm={6} key={skill.paFormId}>
              <MDBox display="flex" alignItems="center" gap={0.5}>
                <MDTypography variant="caption" fontWeight="medium">
                  {index + 1}.
                </MDTypography>
                <MDTypography variant="caption">{skill.skillSet}</MDTypography>
              </MDBox>
            </Grid>
          ))}
        </Grid>
      </MDBox>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!permission || !permission.isOpen) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={0.5}>
          <Alert severity="error">
            <AlertTitle sx={{fontFamily: properties.fontFamily}}>KRA Window Not Open</AlertTitle>
            KRA window is still not opened. Please contact HR for further details.
          </Alert>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (isDeadlinePassed()) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={0.5}>
          <Alert severity="error">
            <AlertTitle>Deadline Passed</AlertTitle>
            KRA Form edit deadline is over. Please contact HR for further details.
          </Alert>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (technicalPrimary.length === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={0.5}>
          <Alert severity="error">
            <AlertTitle>No Skills Available</AlertTitle>
            Before assigning KRA form you need to add technical skills by clicking Performance
            Appraisal → Add Technical Skills
          </Alert>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={0.5} pb={0.5}>
        <Card>
          {/* Header */}
        <MDBox
  variant="gradient"
  sx={{ background: properties.colors.bgcolorbluethem }}
  bgColor="info"
  borderRadius="lg"
  coloredShadow="success"
  mx={1.5}
  mt={1}
  p={1}
  mb={0}
  textAlign="center"
>
  <MDTypography
    variant="h6"
    fontWeight="medium"
    color="white"
    mb={0}   // reduce bottom margin
    lineHeight={1.2} // tighter spacing
  >
    Assign KRA to Employees
  </MDTypography>

  <MDTypography
    variant="caption"
    color="white"
    opacity={0.8}
    mt={0}   // remove top margin
    lineHeight={1.2}
  >
    Select skills and assign to employees (multiple skills per employee)
  </MDTypography>
</MDBox>

          <MDBox p={1}>
            {/* Info Alert */}
            <Alert severity="info" sx={{mb: 1, py: 0.2, "& .MuiAlert-message": {py: 0.2}}}>
              <AlertTitle sx={{mb: 0, fontSize: "0.8rem", lineHeight: 1.4}}>
                KRA Assign Window Open
              </AlertTitle>
              <MDTypography variant="caption">
                Deadline:{" "}
                {new Date(permission.formEditDeadline).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
                {permission.appraisalPeriod && ` (${permission.appraisalPeriod})`}
              </MDTypography>
            </Alert>

            {/* Technical Primary + Employee Selection */}
            <MDBox
              sx={{
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: "8px",
                p: 1.25,
                mb: 1
              }}
            >
              {/* Skill Selection Header */}
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                <MDTypography variant="subtitle2" fontWeight="medium">
                  Select Technical Primary Skill Set <span style={{color: "red"}}>*</span>
                </MDTypography>
                <MDButton variant="text" color="info" size="small" onClick={handleSelectAllSkills}
                  sx={{py: 0, minHeight: 0, fontSize: "0.75rem"}}>
                  {selectedSkills.length === technicalPrimary.length ? "Deselect All" : "Select All"}
                </MDButton>
              </MDBox>

              {/* Skills Checkboxes */}
              <Grid container spacing={0}>
                {technicalPrimary.map(skill => (
                  <Grid item xs={12} sm={6} key={skill.paFormId}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedSkills.includes(skill.paFormId)}
                          onChange={() => handleSkillToggle(skill.paFormId)}
                          size="small"
                          sx={{py: 0.25, pl: 1}}
                        />
                      }
                      label={
                        <MDTypography variant="caption">{skill.skillSet}</MDTypography>
                      }
                      sx={{m: 0, display: "flex", alignItems: "center"}}
                    />
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{my: 1}} />

              {/* Employee Selection */}
              <MDBox mb={1}>
                <MDBox display="flex" alignItems="baseline" gap={0.5} mb={0.5} flexWrap="wrap">
                  <MDTypography variant="subtitle2" fontWeight="medium">
                    Employee List <span style={{color: "red"}}>*</span>
                  </MDTypography>
                  <MDTypography variant="caption" sx={{color: "text.secondary", fontStyle: "italic"}}>
                    (Selected skills will be assigned to all chosen employees)
                  </MDTypography>
                </MDBox>
                <Box sx={{maxWidth: "40%"}}>
                  <Autocomplete
                    multiple
                    options={availableEmployeeOptions}
                    getOptionLabel={option =>
                      `${option.firstName} ${option.middleName || ""} ${option.lastName} (ID: ${option.empId})`.trim()
                    }
                    value={selectedEmployees}
                    onChange={(event, newValue) => setSelectedEmployees(newValue)}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Search and select employees"
                        size="small"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={`${option.firstName} ${option.lastName}`}
                          {...getTagProps({index})}
                          key={option.empId}
                          size="small"
                        />
                      ))
                    }
                    noOptionsText={
                      employeeList.length === 0
                        ? "No employees available for KRA assignment"
                        : "No matching employees"
                    }
                  />
                </Box>
              </MDBox>

              {/* Action Buttons */}
              <MDBox display="flex" justifyContent="flex-end" gap={1}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  onClick={() => navigate(-1)}
                  sx={{py: 0.5}}
                >
                  Cancel
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={handleSubmit}
                  disabled={submitting}
                  sx={{background: properties.colors.bgcolorbluethem, py: 0.5}}
                >
                  {submitting ? "Processing..." : "Send"}
                </MDButton>
              </MDBox>
            </MDBox>

            {/* Read-only skill sections */}
            {renderReadOnlySkillSection("Technical Secondary Skill", technicalSecondary)}
            {technicalSecondary.length > 0 && <Divider sx={{my: 0.75}} />}

            {renderReadOnlySkillSection("Performance Competency Primary Skill Set", competencyPrimary)}
            {competencyPrimary.length > 0 && <Divider sx={{my: 0.75}} />}

            {renderReadOnlySkillSection("Performance Competency Secondary Skill Set", competencySecondary)}
            {competencySecondary.length > 0 && <Divider sx={{my: 0.75}} />}
          </MDBox>
        </Card>

        {/* Notification */}
        {notification.open && (
          <MDBox sx={{position: "fixed", top: 12, right: 12, zIndex: 9999, minWidth: "280px"}}>
            <Alert
              severity={notification.type}
              onClose={() => setNotification({open: false, message: "", type: ""})}
            >
              {notification.message}
            </Alert>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default AssignKRA;